import { useCallback, useEffect, useRef } from 'react';

/**
 * Shared dismissal behaviour for the project modals.
 *
 * Why this exists: the DOM fires `click` on the nearest common ancestor of the mousedown
 * and mouseup targets. In a modal whose card sits *inside* the overlay, selecting text or
 * dragging inside the card and releasing over the overlay therefore dispatches a click on
 * the overlay itself — dismissing the modal and losing the selection or a half-filled
 * form. Putting `stopPropagation` on the card does not help, because the click never
 * targets the card.
 *
 * The fix is to require the press *and* the release to have both landed on the overlay,
 * which is tracked here rather than inferred from the click event alone.
 *
 * Escape always closes (unless disabled). No project modal handled Escape before, and it
 * is the only way out by keyboard once `backdropClose` is false — so it is not optional
 * for the form-bearing modals.
 *
 * Usage — spread onto the element that acts as the backdrop:
 *
 *   const dismiss = useModalDismiss(onClose);
 *   <div style={overlay} {...dismiss}>…</div>
 *
 * @param {Function} onClose                 called to dismiss the modal
 * @param {Object}   [options]
 * @param {boolean}  [options.backdropClose] false disables backdrop dismissal entirely
 *                                           (use for modals containing an editable form)
 * @param {boolean}  [options.enabled]       false while the modal is closed or busy saving
 * @param {boolean}  [options.escapeClose]   false disables the Escape key
 * @returns {{onMouseDown: Function, onMouseUp: Function, onClick: Function}}
 */
export function useModalDismiss(onClose, options = {}) {
  const {
    backdropClose = true,
    enabled = true,
    escapeClose = true,
  } = options;

  const pressedOnBackdrop = useRef(false);
  const releasedOnBackdrop = useRef(false);

  // Held in a ref so call sites can pass an inline arrow without the Escape listener
  // being torn down and re-added on every render.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!enabled || !escapeClose) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && typeof onCloseRef.current === 'function') onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enabled, escapeClose]);

  // mousedown → mouseup → click. Record where the first two landed so the click handler
  // can tell a genuine backdrop click from a drag that merely ended there.
  const onMouseDown = useCallback((e) => {
    pressedOnBackdrop.current = e.target === e.currentTarget;
    releasedOnBackdrop.current = false;
  }, []);

  const onMouseUp = useCallback((e) => {
    releasedOnBackdrop.current = e.target === e.currentTarget;
  }, []);

  const onClick = useCallback(() => {
    const bothOnBackdrop = pressedOnBackdrop.current && releasedOnBackdrop.current;
    pressedOnBackdrop.current = false;
    releasedOnBackdrop.current = false;
    if (!backdropClose || !enabled) return;
    if (bothOnBackdrop && typeof onCloseRef.current === 'function') onCloseRef.current();
  }, [backdropClose, enabled]);

  return { onMouseDown, onMouseUp, onClick };
}

export default useModalDismiss;
